import React, { useState } from 'react';
import HubPageLayout from '../components/HubPageLayout';
import FramedPanel from '../components/FramedPanel';
import { API_BASE_URL, fetchWithCsrf } from '../../../config/api';

// Attribution for the engine behind every result on this page. Typographic on
// purpose — no Reality Defender logo asset ships with the app; drop their mark
// in here if we're given one.
const PoweredByRealityDefender = () => (
  <div className="flex justify-center">
    <span className="inline-flex items-center gap-2.5 border border-black px-4 py-1.5">
      <span className="text-[11px] uppercase tracking-[0.18em] text-gray-600">Powered by</span>
      <span className="text-sm font-bold text-black">Reality Defender</span>
    </span>
  </div>
);

const Deepfake = () => {
  // BFF endpoint for authenticated Reality Defender analysis
  const BFF_REALITYDEFENDER_ENDPOINT = `${API_BASE_URL}/bff/realitydefender-analyze`;

  const [uploadState, setUploadState] = useState({
    isUploading: false,
    result: null,
    error: null,
    selectedFile: null
  });

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadState(prev => ({
        ...prev,
        selectedFile: file,
        result: null,
        error: null
      }));
    }
  };

  const handleUpload = async () => {
    if (!uploadState.selectedFile) return;

    setUploadState(prev => ({
      ...prev,
      isUploading: true,
      error: null,
      result: null
    }));

    try {
      const formData = new FormData();
      formData.append('media', uploadState.selectedFile);

      const response = await fetchWithCsrf(BFF_REALITYDEFENDER_ENDPOINT, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        setUploadState(prev => ({
          ...prev,
          isUploading: false,
          result: data
        }));
      } else {
        throw new Error(data.message || 'Analysis failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadState(prev => ({
        ...prev,
        isUploading: false,
        error: error.message
      }));
    }
  };

  const resetUpload = () => {
    setUploadState({
      isUploading: false,
      result: null,
      error: null,
      selectedFile: null
    });
    // Reset file input
    const fileInput = document.getElementById('media-upload');
    if (fileInput) fileInput.value = '';
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'AUTHENTIC': return 'text-green-600';
      case 'MANIPULATED': return 'text-red-600';
      case 'PROCESSING': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusBg = (status) => {
    switch (status?.toUpperCase()) {
      case 'AUTHENTIC': return 'bg-green-50 border-green-200';
      case 'MANIPULATED': return 'bg-red-50 border-red-200';
      case 'PROCESSING': return 'bg-yellow-50 border-yellow-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <HubPageLayout>
      <FramedPanel title="Truth or lie">
        <div className="px-6 pt-8 pb-6">
          <img
            src="/images/detection/truth-or-lie-hero.jpeg"
            alt=""
            className="w-full h-[300px] object-cover"
          />
        </div>
      </FramedPanel>

      <div className="mt-8 mb-4">
        <PoweredByRealityDefender />
        <p className="mt-6 text-black text-lg leading-relaxed">
          Deepfake detection is powered by Reality Defender, which uses sophisticated algorithms to
          analyze audio, video, images and text for the signs of manipulation scammers use to trick you.
        </p>
      </div>

      {/* Content Section */}
      <div className="px-6 py-4 pb-12">
        <div className="w-[90%] mx-auto">

          {/* Reality Defender Upload Section */}
          <div className="mt-12 border-t border-gray-200 pt-8 mb-12">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-black mb-2">AI-Powered Deepfake Detection</h2>
              <p className="text-gray-600">Upload images, videos, or audio files to analyze for potential manipulation</p>
            </div>

            {/* Upload Interface */}
            <div className="max-w-2xl mx-auto">
              {!uploadState.result && !uploadState.error && (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
                  <div className="mb-4">
                    <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  
                  <input
                    id="media-upload"
                    type="file"
                    className="hidden"
                    accept="image/*,video/*,audio/*,.txt"
                    onChange={handleFileSelect}
                  />
                  
                  <label htmlFor="media-upload" className="cursor-pointer">
                    <div className="text-lg font-medium text-gray-900 mb-1">
                      {uploadState.selectedFile ? uploadState.selectedFile.name : 'Choose a file to analyze'}
                    </div>
                    <div className="text-sm text-gray-500 mb-4">
                      Supports images, videos, audio files, and text • Max 250MB
                    </div>
                    <div className="inline-block bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors">
                      Select File
                    </div>
                  </label>
                </div>
              )}

              {uploadState.selectedFile && !uploadState.result && !uploadState.error && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-blue-900">{uploadState.selectedFile.name}</p>
                      <p className="text-sm text-blue-700">
                        {(uploadState.selectedFile.size / (1024 * 1024)).toFixed(2)} MB • {uploadState.selectedFile.type}
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={resetUpload}
                        className="px-4 py-2 text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUpload}
                        disabled={uploadState.isUploading}
                        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {uploadState.isUploading ? (
                          <div className="flex items-center">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Analyzing...
                          </div>
                        ) : (
                          'Analyze for Deepfakes'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Results Display */}
              {uploadState.result && (
                <div className="mt-6 p-6 bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Analysis Results</h3>
                    <button
                      onClick={resetUpload}
                      className="text-gray-500 hover:text-gray-700 text-sm"
                    >
                      Analyze Another File
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Overall Result */}
                    <div className={`p-4 rounded-lg border-2 ${getStatusBg(uploadState.result.analysis.status)}`}>
                      <h4 className="font-semibold text-gray-900 mb-2">Overall Assessment</h4>
                      <div className={`text-2xl font-bold ${getStatusColor(uploadState.result.analysis.status)} mb-2`}>
                        {uploadState.result.analysis.status || 'Unknown'}
                      </div>
                      <div className="text-sm text-gray-600">
                        Manipulation Likelihood: {uploadState.result.analysis.score ? 
                          `${Math.round(uploadState.result.analysis.score * 100)}%` : 'N/A'}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {uploadState.result.analysis.score ? (
                          uploadState.result.analysis.score < 0.5 ? 
                            'Low probability this media is AI-generated or manipulated' :
                            'High probability this media is AI-generated or manipulated'
                        ) : ''}
                      </div>
                    </div>

                    {/* File Info */}
                    <div className="p-4 bg-gray-50 rounded-lg border">
                      <h4 className="font-semibold text-gray-900 mb-2">File Details</h4>
                      <div className="text-sm text-gray-600 space-y-1">
                        <div><strong>Name:</strong> {uploadState.result.filename}</div>
                        <div><strong>Size:</strong> {(uploadState.result.fileSize / (1024 * 1024)).toFixed(2)} MB</div>
                        <div><strong>Type:</strong> {uploadState.result.fileType}</div>
                      </div>
                    </div>
                  </div>

                  {/* Model Results */}
                  {uploadState.result.analysis.models && uploadState.result.analysis.models.length > 0 && (
                    <div className="mt-6">
                      <h4 className="font-semibold text-gray-900 mb-3">Detailed Model Results</h4>
                      <div className="space-y-2">
                        {uploadState.result.analysis.models.map((model, index) => (
                          <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-md">
                            <span className="font-medium text-gray-900">{model.name}</span>
                            <div className="text-right">
                              <span className={`font-semibold ${getStatusColor(model.status)}`}>
                                {model.status}
                              </span>
                              {model.score && (
                                <div className="text-sm text-gray-500">
                                  {Math.round(model.score * 100)}%
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Error Display */}
              {uploadState.error && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-red-900 mb-2">Analysis Failed</h3>
                      <p className="text-red-700">{uploadState.error}</p>
                    </div>
                    <button
                      onClick={resetUpload}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Try Again
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
    </HubPageLayout>
  );
};

export default Deepfake; 